<template>
    <article>
        <p>
            In the current cloud-native era, almost every organization has one or more Kubernetes clusters in their production infrastructures.
            However, only a small percentage of companies, especially enterprise-level ones, can claim that they are fully committed to running everything exclusively on Kubernetes.
            The most typical scenario is that new stateless services are deployed on Kubernetes, while legacy applications, third-party services, and databases continue to run on dedicated VMs or bare-metal nodes.
        </p>

        <p>
            To effectively monitor and understand a system, it's essential to have a complete view without any blind spots.
        </p>

        <p>
            Many Coroot users are happy using it to monitor apps in Kubernetes clusters, but they find it difficult to deploy Coroot in non-Kubernetes environments.
            We're pleased to announce that Coroot 1.0 now makes it easier to monitor heterogeneous infrastructures.
        </p>

        <p>
            Now it's easier to add standalone nodes to a Coroot instance in Kubernetes.
            The agent, deployed as either a systemd service or Docker container on Virtual Machines or bare-metal servers,
            sends telemetry data (metrics, logs, traces, and profiles) directly to the Coroot instance.
            This eliminates the need to configure Prometheus to discover these nodes—just configure the agents with the Coroot endpoint.
        </p>

        <div class="my-6">
            <v-img class="mx-auto" max-width="500px" eager src="/static/img/blog/coroot-on-vm/coroot-in-k8s.svg" />
        </div>

        <p>
            As a result, you will have consistent observability regardless of whether services are deployed in Kubernetes or not.
        </p>

        <p>
            The second improvement is that you can now deploy the entire Coroot stack using Docker Compose or Docker Swarm, in addition to Kubernetes.
        </p>

        <div class="my-6">
            <v-img class="mx-auto" max-width="500px" eager src="/static/img/blog/coroot-on-vm/coroot-on-vm.svg" />
        </div>

        <p>
            So, even if you're not using Kubernetes, deploying Coroot on a dedicated VM gives you full system visibility without needing to switch to modern tech stacks.
        </p>

        <h2>Demo</h2>

        <p>
            Let's remember how applications used to be before Kubernetes became popular.
            Think of an app and its database installed as DEB packages directly on Linux machines (Ah, I sometimes miss those days).
        </p>

        <div class="my-6">
            <v-img class="mx-auto" max-width="600px" eager src="/static/img/blog/coroot-on-vm/demo-app.svg" />
        </div>

        <p>
            Now let's install Coroot on an Ubuntu VM to make this app observable.
        </p>

        <p>Step #1: install Docker Compose</p>

        <CodeSnippet>
        <pre v-hl.bash>
apt update && apt install -y docker-compose-v2
        </pre>
        </CodeSnippet>

        <p>Step #2: install Coroot</p>

        <CodeSnippet>
        <pre v-hl.bash>
curl -fsS https://raw.githubusercontent.com/coroot/coroot/main/deploy/docker-compose.yaml | docker compose -f - up -d
        </pre>
        </CodeSnippet>

        <p>
            That's it, now we can access Coroot at http://&lt;NODE_IP&gt;:8080/.
            Currently, Coroot is monitoring only this specific node:
        </p>

        <v-card class="pa-5 my-6">
            <v-img class="mx-auto" eager src="/static/img/blog/coroot-on-vm/coroot-nodes1.png" />
        </v-card>

        <p>
            Instructions on how to add other nodes are available by clicking the "Add nodes" button:
        </p>

        <v-card class="pa-5 my-6">
            <v-img class="mx-auto" eager src="/static/img/blog/coroot-on-vm/add-node.png" />
        </v-card>

        <p>Step #3: install the agent to the application and DB nodes:</p>

        <CodeSnippet>
        <pre v-hl.bash>
curl -sfL https://raw.githubusercontent.com/coroot/coroot-node-agent/main/install.sh | \
  COLLECTOR_ENDPOINT=http://192.168.100.2:8080 \
  API_KEY=xykptdnr \
  SCRAPE_INTERVAL=15s \
  sh -
        </pre>
        </CodeSnippet>

        <p>
            A minute later, we can see that the app instances and the Postgres database have appeared in the Coroot UI.
            All Coroot capabilities are fully supported.
        </p>

        <v-card class="pa-5 my-6">
            <v-img class="mx-auto" eager src="/static/img/blog/coroot-on-vm/coroot-app.png" />
        </v-card>

        <h2>Conclusion</h2>

        <p>
            We at Coroot work hard to make even complex systems observable!
            Coroot v1.0 is a huge milestone for us, and we believe its new features will enable you to troubleshoot like a ninja.
        </p>

        <p>
            Coroot v1.0.0 is already available, so you can try it out right now.
        </p>

        <p>
            Try <router-link :to="{name: 'cloud'}">Coroot Cloud</router-link> now (14-day free trial is available) or follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to install Coroot Community Edition.
        </p>

        <p>
            If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️ –
            it helps us reach more engineers and improve our product for you.
        </p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>
    </article>
</template>

<script>


export default {
    components: {},
    data() {
        return {
            installation: null,
        };
    },
}
</script>

<style scoped>
</style>
